import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PowerService, PowerFacility } from '../../../services/powerService';
import { supabase } from '../../auth/AuthProvider';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { ColumnOption } from '../types';
import FacilitiesSearchForm from './FacilitiesSearchForm';
import FacilitiesTable from './FacilitiesTable';
import FacilitiesSearchFilter from './FacilitiesSearchFilter';

interface PowerFacilitiesProps {
  defaultCountryCode?: string;
}

const PowerFacilities: React.FC<PowerFacilitiesProps> = ({ defaultCountryCode }) => {
  const [countryOptions, setCountryOptions] = useState<Record<string, string> | null>(null);
  const [facilityTypes, setFacilityTypes] = useState<string[] | null>(null);
  const [facilities, setFacilities] = useState<PowerFacility[] | null>(null);
  const [filteredFacilities, setFilteredFacilities] = useState<PowerFacility[] | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showColumnSettings, setShowColumnSettings] = useState<boolean>(false);

  // Filter states
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [minCapacity, setMinCapacity] = useState<string>('');
  const [maxCapacity, setMaxCapacity] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({});

  const statuses = ['operating', 'announced', 'construction', 'cancelled', 'mothballed', 'retired'];

  // Available columns definition
  const columnOptions: ColumnOption[] = useMemo(
    () => [
      { key: 'Plant / Project name', label: 'Name', defaultVisible: true },
      { key: 'Type', label: 'Type', defaultVisible: true },
      { key: 'Country/area', label: 'Country', defaultVisible: true },
      { key: 'Capacity (MW)', label: 'Capacity (MW)', defaultVisible: true },
      { key: 'Status', label: 'Status', defaultVisible: true },
      { key: 'Technology', label: 'Technology', defaultVisible: false },
      { key: 'Start year', label: 'Build Year', defaultVisible: false },
      { key: 'Owner', label: 'Owner', defaultVisible: false },
      { key: 'Subregion', label: 'Subregion', defaultVisible: false },
      { key: 'Region', label: 'Region', defaultVisible: false },
      { key: 'City', label: 'City', defaultVisible: false },
      { key: 'Latitude', label: 'Latitude', defaultVisible: false },
      { key: 'Longitude', label: 'Longitude', defaultVisible: false },
      { key: 'Subnational unit (state, province)', label: 'State/Province', defaultVisible: false },
      { key: 'Fuel', label: 'Fuel', defaultVisible: false },
    ],
    [],
  );

  // Initialize with default column settings
  const initializeDefaultColumns = useCallback(() => {
    const defaults = columnOptions.reduce(
      (acc, col) => {
        acc[col.key] = col.defaultVisible;
        return acc;
      },
      {} as Record<string, boolean>,
    );

    setVisibleColumns(defaults);
    localStorage.setItem('powerFacilitiesColumns', JSON.stringify(defaults));
  }, [columnOptions]);

  // Load column visibility settings
  useEffect(() => {
    const savedColumns = localStorage.getItem('powerFacilitiesColumns');

    if (savedColumns) {
      try {
        setVisibleColumns(JSON.parse(savedColumns));
      } catch {
        // If parse fails, reset to defaults
        initializeDefaultColumns();
      }
    } else {
      initializeDefaultColumns();
    }
  }, [initializeDefaultColumns]);

  // Save column visibility settings
  const saveColumnSettings = (settings: Record<string, boolean>) => {
    setVisibleColumns(settings);
    localStorage.setItem('powerFacilitiesColumns', JSON.stringify(settings));
  };

  // Toggle a single column's visibility
  const toggleColumn = (key: string) => {
    const updatedColumns = { ...visibleColumns, [key]: !visibleColumns[key] };
    saveColumnSettings(updatedColumns);
  };

  // Reset columns to default settings
  const resetColumnsToDefault = () => {
    initializeDefaultColumns();
    setShowColumnSettings(false);
  };

  // Set default country if available
  useEffect(() => {
    if (defaultCountryCode && countryOptions) {
      const countryName = countryOptions[defaultCountryCode];
      if (countryName) {
        setSelectedCountry(countryName);
      }
    }
  }, [defaultCountryCode, countryOptions]);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const getToken = async () => {
          const { data } = await supabase.auth.getSession();
          const token = data.session?.access_token;
          if (!token) {
            throw new Error('No authentication token available');
          }
          return token;
        };

        const powerService = new PowerService(import.meta.env.VITE_API_BASE_URL, getToken);

        // Fetch countries and facility types
        const [countriesResponse, typesResponse] = await Promise.all([
          powerService.getPowerForecastCountries(),
          powerService.getPowerFacilityTypes(),
        ]);

        if (countriesResponse.error || !countriesResponse.data) {
          throw new Error(countriesResponse.error || 'Failed to fetch countries');
        }

        if (typesResponse.error || !typesResponse.data) {
          throw new Error(typesResponse.error || 'Failed to fetch facility types');
        }

        setCountryOptions(countriesResponse.data);
        setFacilityTypes(typesResponse.data);

        // Set an initial country to prevent fetching all facilities
        const initialCountry = defaultCountryCode
          ? countriesResponse.data[defaultCountryCode]
          : Object.values(countriesResponse.data)[0];

        setSelectedCountry(initialCountry);

        // Fetch initial facilities data with the first country
        const facilitiesResponse = await powerService.getPowerFacilities({
          country: initialCountry,
        });

        if (facilitiesResponse.error || !facilitiesResponse.data) {
          throw new Error(facilitiesResponse.error || 'Failed to fetch power facilities');
        }

        setFacilities(facilitiesResponse.data.facilities);
        setFilteredFacilities(facilitiesResponse.data.facilities);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [defaultCountryCode]);

  // Filter facilities based on search query
  useEffect(() => {
    if (!facilities) return;

    if (!searchQuery.trim()) {
      setFilteredFacilities(facilities);
      return;
    }

    const query = searchQuery.toLowerCase().trim();

    const filtered = facilities.filter((facility) => {
      // Search through all facility properties that are strings
      return Object.entries(facility).some(([, value]) => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(query);
        }
        if (typeof value === 'number') {
          return value.toString().includes(query);
        }
        return false;
      });
    });

    setFilteredFacilities(filtered);
  }, [facilities, searchQuery]);

  // Handler for filter search
  const handleFilter = async () => {
    if (!selectedCountry) {
      setError('Please select a country to search');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const getToken = async () => {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        if (!token) {
          throw new Error('No authentication token available');
        }
        return token;
      };

      const powerService = new PowerService(import.meta.env.VITE_API_BASE_URL, getToken);

      const params: any = {
        country: selectedCountry,
      };

      if (selectedType) params.facility_type = selectedType;
      if (selectedStatus) params.status = selectedStatus;
      if (minCapacity && !isNaN(Number(minCapacity))) params.min_capacity = Number(minCapacity);
      if (maxCapacity && !isNaN(Number(maxCapacity))) params.max_capacity = Number(maxCapacity);

      const facilitiesResponse = await powerService.getPowerFacilities(params);

      if (facilitiesResponse.error || !facilitiesResponse.data) {
        throw new Error(facilitiesResponse.error || 'Failed to fetch power facilities');
      }

      setFacilities(facilitiesResponse.data.facilities);
      setFilteredFacilities(facilitiesResponse.data.facilities);
      setSearchQuery('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading spinner while fetching initial data
  if (isLoading && (!countryOptions || !facilityTypes)) {
    return (
      <div className="flex justify-center py-10">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <FacilitiesSearchForm
        selectedCountry={selectedCountry}
        setSelectedCountry={setSelectedCountry}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        minCapacity={minCapacity}
        setMinCapacity={setMinCapacity}
        maxCapacity={maxCapacity}
        setMaxCapacity={setMaxCapacity}
        handleFilter={handleFilter}
        countryOptions={countryOptions}
        facilityTypes={facilityTypes}
        statuses={statuses}
      />

      {isLoading && (facilities || countryOptions) ? (
        <div className="flex justify-center py-10">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {filteredFacilities && (
            <div className="mt-4">
              <FacilitiesSearchFilter
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                resultsCount={filteredFacilities.length}
                totalCount={facilities?.length || 0}
              />

              <FacilitiesTable
                facilities={filteredFacilities}
                columnOptions={columnOptions}
                visibleColumns={visibleColumns}
                isLoading={isLoading}
                showColumnSettings={showColumnSettings}
                setShowColumnSettings={setShowColumnSettings}
                toggleColumn={toggleColumn}
                resetColumnsToDefault={resetColumnsToDefault}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PowerFacilities;
